import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { UserPlus, DollarSign, Zap, Package, TrendingUp, TrendingDown, Users, BarChart3 } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';

const PERIODS = [
  { key: '1d', label: '1 jour', source: 'daily', slice: 1 },
  { key: '1w', label: '1 semaine', source: 'daily', slice: 7 },
  { key: '1m', label: '1 mois', source: 'daily', slice: 30 },
  { key: '3m', label: '3 mois', source: 'weekly', slice: 13 },
  { key: '6m', label: '6 mois', source: 'weekly', slice: 26 },
  { key: '1y', label: '1 an', source: 'monthly', slice: 12 },
];

function getDataForPeriod(growthData, periodKey) {
  const period = PERIODS.find(p => p.key === periodKey);
  if (!period || !growthData) return [];
  const source = growthData[period.source] || [];
  return source.slice(-period.slice);
}

function SummaryCard({ icon: Icon, label, value, change, color }) {
  const isPositive = change > 0;
  const colors = {
    blue: 'bg-blue-50 text-[#1F3F72] dark:bg-blue-900/30 dark:text-blue-300',
    orange: 'bg-orange-50 text-[#F25C05] dark:bg-orange-900/30 dark:text-orange-300',
    green: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300',
  };
  return (
    <div className="bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        {change !== undefined && change !== null && (
          <div className={`flex items-center gap-1 text-xs font-medium ${isPositive ? 'text-emerald-600' : change < 0 ? 'text-red-500' : 'text-neutral-400'}`}>
            {isPositive ? <TrendingUp className="w-3.5 h-3.5" /> : change < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : null}
            {isPositive ? '+' : ''}{change}
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-neutral-900 dark:text-white">{value}</p>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{label}</p>
    </div>
  );
}

export function StatsTab({ growthData, statsPeriod, setStatsPeriod, salesStats }) {
  const data = useMemo(() => getDataForPeriod(growthData, statsPeriod), [growthData, statsPeriod]);

  const totals = useMemo(() => {
    if (!data.length) return { users: 0, listings: 0, revenue: 0, boosts: 0 };
    return data.reduce((acc, d) => ({
      users: acc.users + (d.users || 0),
      listings: acc.listings + (d.listings || 0),
      revenue: acc.revenue + (d.revenue || 0),
      boosts: acc.boosts + (d.boosts || 0),
    }), { users: 0, listings: 0, revenue: 0, boosts: 0 });
  }, [data]);

  const periodLabel = PERIODS.find(p => p.key === statsPeriod)?.label || '';

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center gap-2 flex-wrap">
        <BarChart3 className="w-4 h-4 text-neutral-400" />
        <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Periode :</span>
        {PERIODS.map(p => (
          <Button key={p.key} size="sm" variant={statsPeriod === p.key ? 'default' : 'outline'}
            className={`text-xs ${statsPeriod === p.key ? 'bg-[#1F3F72]' : ''}`}
            onClick={() => setStatsPeriod(p.key)} data-testid={`stats-period-${p.key}`}>
            {p.label}
          </Button>
        ))}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard icon={Users} label={`Nouveaux utilisateurs (${periodLabel})`} value={totals.users} change={totals.users} color="blue" />
        <SummaryCard icon={Package} label={`Nouvelles annonces (${periodLabel})`} value={totals.listings} change={totals.listings} color="orange" />
        <SummaryCard icon={DollarSign} label={`Revenus (${periodLabel})`} value={`${totals.revenue.toFixed(2)}€`} change={totals.revenue > 0 ? `${totals.revenue.toFixed(0)}€` : null} color="green" />
        <SummaryCard icon={Zap} label={`Boosts vendus (${periodLabel})`} value={totals.boosts} change={totals.boosts} color="purple" />
      </div>

      {/* Growth Chart */}
      <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl p-6">
        <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[#1F3F72]" /> Croissance
        </h3>
        <p className="text-xs text-neutral-400 mb-4">Nouveaux utilisateurs et annonces — {periodLabel}</p>
        <div style={{ height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Legend />
              <Bar dataKey="users" name="Utilisateurs" fill="#1F3F72" radius={[4,4,0,0]} />
              <Bar dataKey="listings" name="Annonces" fill="#F25C05" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl p-6">
        <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-500" /> Revenus
        </h3>
        <p className="text-xs text-neutral-400 mb-4">Revenus des boosts en EUR — {periodLabel}</p>
        <div style={{ height: 350 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(val) => `${Number(val).toFixed(2)} €`} />
              <Legend />
              <Area type="monotone" dataKey="revenue" name="Revenus (€)" stroke="#10b981" fill="url(#revenueGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Boosts Chart */}
      <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl p-6">
        <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-500" /> Boosts vendus
        </h3>
        <p className="text-xs text-neutral-400 mb-4">Nombre de boosts — {periodLabel}</p>
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Legend />
              <Line type="monotone" dataKey="boosts" name="Boosts" stroke="#F25C05" strokeWidth={2} dot={{ r: 4, fill: '#F25C05' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sales Stats */}
      {salesStats && (
        <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl p-6">
          <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" /> Compteur de ventes
          </h3>
          <p className="text-xs text-neutral-400 mb-4">Suivi des annonces supprimees et leurs raisons</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-emerald-600">{salesStats.sold_via_platform}</p>
              <p className="text-[10px] text-emerald-700 font-medium mt-0.5">Vendus via DestockPro</p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{salesStats.sold_elsewhere}</p>
              <p className="text-[10px] text-blue-700 font-medium mt-0.5">Vendus ailleurs</p>
            </div>
            <div className="bg-neutral-50 dark:bg-neutral-700/30 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-neutral-600">{salesStats.not_available}</p>
              <p className="text-[10px] text-neutral-500 font-medium mt-0.5">Plus disponible</p>
            </div>
            <div className="bg-neutral-50 dark:bg-neutral-700/30 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-neutral-600">{salesStats.other}</p>
              <p className="text-[10px] text-neutral-500 font-medium mt-0.5">Autre raison</p>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-[#F25C05]">{salesStats.total_deleted}</p>
              <p className="text-[10px] text-orange-700 font-medium mt-0.5">Total supprimees</p>
            </div>
          </div>
          {salesStats.sold_via_platform > 0 && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg px-4 py-3">
              <p className="text-sm font-semibold text-emerald-700">
                {Math.round(salesStats.sold_via_platform / Math.max(salesStats.total_deleted, 1) * 100)}% des suppressions sont des ventes realisees via DestockPro
              </p>
            </div>
          )}
          {salesStats.recent?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-neutral-500 mb-2">Dernieres suppressions</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {salesStats.recent.map((d) => (
                  <div key={d.title + '_' + (d.deleted_at || d.reason)} className="flex items-center justify-between text-xs px-3 py-2 bg-neutral-50 dark:bg-neutral-700/30 rounded-lg">
                    <span className="font-medium text-neutral-700 dark:text-neutral-300 truncate max-w-[200px]">{d.title}</span>
                    <span className={`font-semibold whitespace-nowrap ml-2 ${
                      d.reason === 'sold_destockpro' ? 'text-emerald-600' :
                      d.reason === 'sold_elsewhere' ? 'text-blue-600' : 'text-neutral-500'
                    }`}>
                      {d.reason === 'sold_destockpro' ? 'Vendu via DestockPro' :
                       d.reason === 'sold_elsewhere' ? 'Vendu ailleurs' :
                       d.reason === 'not_available' ? 'Plus disponible' :
                       d.reason === 'other' ? 'Autre' : 'Non precise'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
